import React, { useState, useEffect } from 'react';
import { Package, AlertTriangle, PieChart, History, ShoppingCart, Truck, FileText, Package2, AlertCircle, Shield, ShieldAlert, AlertOctagon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { useInventory } from '../../context/InventoryContext';
import { supabase } from '../../lib/supabase';

// Define the stats we need to fetch from the database
interface DashboardStats {
  salesOrders: number;
  purchaseOrders: number;
  pendingShipments: number;
  itemsToReceive: number;
  weOweItems: number;
  activeWarranties: number;
  expiringWarranties: number;
  warrantyClaims: number;
}

const DashboardSummary: React.FC = () => {
  const { getDashboardSummary, products, hasStockMismatch } = useInventory();
  const [stats, setStats] = useState<DashboardStats>({
    salesOrders: 0,
    purchaseOrders: 0,
    pendingShipments: 0,
    itemsToReceive: 0,
    weOweItems: 0,
    activeWarranties: 0,
    expiringWarranties: 0,
    warrantyClaims: 0
  });
  const [manufacturedProductsCount, setManufacturedProductsCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const summary = getDashboardSummary();
  const stockMismatchCount = products.filter(product => hasStockMismatch(product)).length;

  useEffect(() => {
    loadRealTimeStats();
  }, []);

  const loadRealTimeStats = async () => {
    try {
      // Load manufactured products count
      try {
        const { count: productsCount, error: productsError } = await supabase
          .from('manufactured_products')
          .select('*', { count: 'exact', head: true });

        if (productsError && productsError.code === '42P01') {
          console.log('Manufactured products table not yet created. Please run the migration to create it.');
          setManufacturedProductsCount(0);
        } else if (productsError) {
          throw productsError;
        } else {
          setManufacturedProductsCount(productsCount || 0);
        }
      } catch (tableError: any) {
        if (tableError.code === '42P01') {
          console.log('Manufactured products table not yet created. Please run the migration to create it.');
          setManufacturedProductsCount(0);
        } else {
          throw tableError;
        }
      }

      // Load actual sales orders count
      const { count: salesCount, error: salesError } = await supabase
        .from('sales_orders')
        .select('*', { count: 'exact', head: true });

      if (salesError) throw salesError;

      // Load actual purchase orders count
      const { count: purchaseCount, error: purchaseError } = await supabase
        .from('purchase_orders')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'ordered');

      if (purchaseError) throw purchaseError;

      // Load actual pending shipments count
      const { count: shipmentsCount, error: shipmentsError } = await supabase
        .from('shipping_info')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');

      if (shipmentsError) throw shipmentsError;

      // Load actual items to receive count (purchase order items that are not fully received)
      const { count: receivingCount, error: receivingError } = await supabase
        .from('purchase_order_items')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');

      if (receivingError) throw receivingError;

      // Load We Owe items count (pending items)
      const { count: weOweCount, error: weOweError } = await supabase
        .from('we_owe_items')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');

      if (weOweError) throw weOweError;

      // Load active warranties count (sold products with warranty end date in the future)
      const now = new Date().toISOString();
      const { count: activeWarrantiesCount, error: warrantyError } = await supabase
        .from('sold_products')
        .select('*', { count: 'exact', head: true })
        .gt('warranty_end_date', now);

      if (warrantyError) throw warrantyError;

      // Load expiring warranties (within 30 days)
      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

      const { count: expiringWarrantiesCount, error: expiringError } = await supabase
        .from('sold_products')
        .select('*', { count: 'exact', head: true })
        .gt('warranty_end_date', now)
        .lte('warranty_end_date', thirtyDaysFromNow.toISOString());

      if (expiringError) throw expiringError;

      // Load active warranty claims count
      const { count: warrantyClaimsCount, error: claimsError } = await supabase
        .from('warranty_claims')
        .select('*', { count: 'exact', head: true })
        .in('status', ['pending', 'approved', 'in_progress']);

      if (claimsError) throw claimsError;

      setStats({
        salesOrders: salesCount || 0,
        purchaseOrders: purchaseCount || 0,
        pendingShipments: shipmentsCount || 0,
        itemsToReceive: receivingCount || 0,
        weOweItems: weOweCount || 0,
        activeWarranties: activeWarrantiesCount || 0,
        expiringWarranties: expiringWarrantiesCount || 0,
        warrantyClaims: warrantyClaimsCount || 0
      });
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
      // Keep default values on error
    } finally {
      setLoading(false);
    }
  };

  const dashboardStats = [
    {
      name: 'Total Supplies',
      value: summary.totalProducts,
      icon: <Package className="h-8 w-8 text-blue-900" />,
      color: 'bg-blue-50 text-blue-900',
      link: '/supplies',
    },
    {
      name: 'Total Inventory',
      value: summary.totalStock,
      icon: <PieChart className="h-8 w-8 text-emerald-600" />,
      color: 'bg-emerald-50 text-emerald-600',
      link: '/supplies',
    },
    {
      name: 'Low Stock Supplies',
      value: summary.lowStockCount,
      icon: <AlertTriangle className="h-8 w-8 text-amber-600" />,
      color: 'bg-amber-50 text-amber-600',
      link: '/supplies?filter=low',
    },
    {
      name: 'Recent Transactions',
      value: summary.recentTransactions.length,
      icon: <History className="h-8 w-8 text-purple-600" />,
      color: 'bg-purple-50 text-purple-600',
      link: '/transactions',
    },
    {
      name: 'Sales Orders',
      value: loading ? '...' : stats.salesOrders,
      icon: <ShoppingCart className="h-8 w-8 text-green-600" />,
      color: 'bg-green-50 text-green-600',
      link: '/sales-orders',
    },
    {
      name: 'Purchase Orders',
      value: loading ? '...' : stats.purchaseOrders,
      icon: <FileText className="h-8 w-8 text-indigo-600" />,
      color: 'bg-indigo-50 text-indigo-600',
      link: '/purchase-orders',
    },
    {
      name: 'Manufactured Products',
      value: loading ? '...' : manufacturedProductsCount,
      icon: <Package2 className="h-8 w-8 text-purple-600" />,
      color: 'bg-purple-50 text-purple-600',
      link: '/products',
    },
    {
      name: 'Pending Shipments',
      value: loading ? '...' : stats.pendingShipments,
      icon: <Truck className="h-8 w-8 text-orange-600" />,
      color: 'bg-orange-50 text-orange-600',
      link: '/shipping',
    },
    {
      name: 'Items to Receive',
      value: loading ? '...' : stats.itemsToReceive,
      icon: <Package2 className="h-8 w-8 text-cyan-600" />,
      color: 'bg-cyan-50 text-cyan-600',
      link: '/receiving',
    },
    {
      name: 'Stock Mismatches',
      value: stockMismatchCount,
      icon: <AlertTriangle className="h-8 w-8 text-red-600" />,
      color: 'bg-red-50 text-red-600',
      link: '/mismatch-inventory',
    },
    {
      name: 'We Owe Items',
      value: loading ? '...' : stats.weOweItems,
      icon: <AlertCircle className="h-8 w-8 text-amber-600" />,
      color: 'bg-amber-50 text-amber-600',
      link: '/sales-orders?filter=we-owe',
    },
    {
      name: 'Active Warranties',
      value: loading ? '...' : stats.activeWarranties,
      icon: <Shield className="h-8 w-8 text-sky-950" />,
      color: 'bg-sky-50 text-sky-950',
      link: '/warranties',
    },
    {
      name: 'Expiring Soon',
      value: loading ? '...' : stats.expiringWarranties,
      icon: <ShieldAlert className="h-8 w-8 text-yellow-950" />,
      color: 'bg-yellow-50 text-yellow-950',
      link: '/warranties?filter=expiring',
    },
    {
      name: 'Warranty Claims',
      value: loading ? '...' : stats.warrantyClaims,
      icon: <AlertOctagon className="h-8 w-8 text-red-950" />,
      color: 'bg-red-50 text-red-950',
      link: '/warranties?tab=claims',
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
      {dashboardStats.map((stat) => (
        <Link 
          key={stat.name} 
          to={stat.link}
          className="block"
        >
          <Card className="overflow-hidden transition-all duration-200 hover:shadow-md hover:bg-gray-50 cursor-pointer h-full">
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className={`p-3 rounded-full ${stat.color} dark:bg-opacity-90 dark:backdrop-blur-sm`}>
                  {stat.icon}
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">{stat.name}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>
      ))}
    </div>
  );
};

export default DashboardSummary;